import type { UserSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";
import { ApiRoles } from "@/app/shared/services/volateq-api/api-roles";
import { OptionalFeatureAccess, OptionalFeatureHelper } from "./optional-feature-helper";

/** General VolaTeQ sales WhatsApp number, in the digits-only form wa.me expects. */
export const PRICING_WHATSAPP_NUMBER = "34611057760";
export const PRICING_WHATSAPP_NUMBER_DISPLAY = "+34 611 057 760";

export enum PricingPackageId {
  FLEXIBLE = "flexible",
  STARTER = "starter",
  GROWTH = "growth",
  PROFESSIONAL = "professional",
  ENTERPRISE = "enterprise",
}

export interface PricingPackage {
  id: PricingPackageId;
  /** Product tier names are not translated. */
  name: string;
  icon: string;
  /** Credits included per year. null for pay-as-you-go (FLEXIBLE) and custom pricing (ENTERPRISE). */
  credits: number | null;
  /** EUR per credit for FLEXIBLE, EUR per year for the annual packages, null for custom pricing. */
  priceEur: number | null;
  pricePerMwpEur: number | null;
  highlighted: boolean;
}

export const PRICING_PACKAGES: PricingPackage[] = [
  {
    id: PricingPackageId.FLEXIBLE,
    name: "Flexible",
    icon: "sliders",
    credits: null,
    priceEur: 350,
    pricePerMwpEur: 350,
    highlighted: false,
  },
  {
    id: PricingPackageId.STARTER,
    name: "Starter",
    icon: "lightning-charge-fill",
    credits: 5,
    priceEur: 1000,
    pricePerMwpEur: 200,
    highlighted: false,
  },
  {
    id: PricingPackageId.GROWTH,
    name: "Growth",
    icon: "graph-up",
    credits: 25,
    priceEur: 3000,
    pricePerMwpEur: 120,
    highlighted: true,
  },
  {
    id: PricingPackageId.PROFESSIONAL,
    name: "Professional",
    icon: "trophy-fill",
    credits: 50,
    priceEur: 5500,
    pricePerMwpEur: 110,
    highlighted: false,
  },
  {
    id: PricingPackageId.ENTERPRISE,
    name: "Enterprise",
    icon: "building",
    credits: null,
    priceEur: null,
    pricePerMwpEur: null,
    highlighted: false,
  },
];

/** Enterprise is priced individually, so it has nothing to compare in the package details. */
export const PRICING_DETAIL_PACKAGES: PricingPackage[] = PRICING_PACKAGES.filter(
  pricingPackage => pricingPackage.id !== PricingPackageId.ENTERPRISE,
);

export enum PricingDetailKind {
  GSD = "gsd",
  CAPACITY = "capacity",
  PRICE_PER_MWP = "price-per-mwp",
  INCLUDED = "included",
  ADD_ON = "add-on",
}

export interface PricingDetailRow {
  /** Translation keys: "pricing-details-<id>", "pricing-details-<id>_expl" and, with hasBadge, "pricing-details-<id>-badge". */
  id: string;
  kind: PricingDetailKind;
  hasBadge: boolean;
}

export const PRICING_DETAIL_ROWS: PricingDetailRow[] = [
  { id: "gsd", kind: PricingDetailKind.GSD, hasBadge: false },
  { id: "capacity", kind: PricingDetailKind.CAPACITY, hasBadge: false },
  { id: "price", kind: PricingDetailKind.PRICE_PER_MWP, hasBadge: false },
  { id: "license", kind: PricingDetailKind.INCLUDED, hasBadge: true },
  { id: "iec", kind: PricingDetailKind.INCLUDED, hasBadge: true },
  { id: "ai", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "quality-control", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "anomalies", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "production-loss", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "thermal-ortho", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "rgb-ortho", kind: PricingDetailKind.INCLUDED, hasBadge: false },
  { id: "soiling", kind: PricingDetailKind.ADD_ON, hasBadge: true },
];

export type PricingDetailCell =
  | { type: "included" }
  | { type: "text"; key: string; params?: Record<string, number> }
  | { type: "price-per-mwp"; amountEur: number };

export class PricingHelper {
  /** Same rule as the backend (CustomerService.can_see_pricing): the owners of a customer Volateq enabled
   * pricing for, and Volateq admins as an admin feature. A Volateq admin switched to such a customer sees it
   * like its owners do. `isAdmin` is false for a hidden super admin. */
  static getPricingAccess(
    user: Pick<UserSchema, "roles" | "customer">,
    isAdmin: boolean,
    isVolateqAdmin: boolean,
  ): OptionalFeatureAccess {
    const enabledForCustomer = !!user.customer?.enable_pricing &&
      (isVolateqAdmin || user.roles.some(role => role.id === ApiRoles.CUSTOMER_OWNER));

    return OptionalFeatureHelper.getAccess(enabledForCustomer, isAdmin);
  }

  /** Same rule as the backend (CustomerService.can_see_wallet_credits): Volateq admins and customer owners,
   * independent of enable_pricing, as a customer can have a Wallet without seeing the pricing page. */
  static canSeeWalletCredits(user: Pick<UserSchema, "roles">, isVolateqAdmin: boolean): boolean {
    return isVolateqAdmin || user.roles.some(role => role.id === ApiRoles.CUSTOMER_OWNER);
  }

  /** Orders only store the booked credits, so the annual package is the one with the same credits,
   * e.g. "Growth (25 Credits)". Individual credit counts have no package: "30 Credits". */
  static annualPlanLabel(bookedCredits: number, creditsLabel: string): string {
    const credits = `${bookedCredits} ${creditsLabel}`;
    const annualPackage = PRICING_PACKAGES.find(pricingPackage => pricingPackage.credits === bookedCredits);

    return annualPackage ? `${annualPackage.name} (${credits})` : credits;
  }

  static getPackage(id: PricingPackageId): PricingPackage {
    return PRICING_PACKAGES.find(pricingPackage => pricingPackage.id === id)!;
  }

  static detailCell(row: PricingDetailRow, pricingPackage: PricingPackage): PricingDetailCell {
    switch (row.kind) {
      case PricingDetailKind.INCLUDED:
        return { type: "included" };
      case PricingDetailKind.GSD:
        return { type: "text", key: "pricing-details-gsd-value" };
      case PricingDetailKind.ADD_ON:
        return { type: "text", key: "pricing-details-" + row.id + "-value" };
      case PricingDetailKind.CAPACITY:
        return PricingHelper.capacityCell(pricingPackage);
      case PricingDetailKind.PRICE_PER_MWP:
        return pricingPackage.pricePerMwpEur === null
          ? { type: "text", key: "pricing-custom-pricing" }
          : { type: "price-per-mwp", amountEur: pricingPackage.pricePerMwpEur };
    }
  }

  static capacityCell(pricingPackage: PricingPackage): PricingDetailCell {
    return pricingPackage.credits === null
      ? { type: "text", key: "pricing-details-pay-as-you-go" }
      : { type: "text", key: "pricing-details-mwp", params: { mwp: pricingPackage.credits } };
  }

  static formatEur(amount: number, locale: string): string {
    return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);
  }

  static fullName(user: { first_name: string; last_name: string }): string {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }

  static contactMessage(greeting: string, request: string, closingLine: string | null): string {
    return [greeting, "", request, ...(closingLine ? ["", closingLine] : [])].join("\n");
  }

  static mailtoLink(email: string, subject: string, body: string): string {
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  static whatsAppLink(text: string): string {
    return `https://wa.me/${PRICING_WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  }
}
